'use client'

import type { PublicSeminarDetailResponse } from '@tebuto/api/public/models/PublicSeminarDetailResponse'
import type { PublicSeminarOccurrenceSummary } from '@tebuto/api/public/models/PublicSeminarOccurrenceSummary'
import { Button } from '@tebuto/buttons/Button'
import { cn } from '@tebuto/lib/utils'
import { CalendarDays, ChevronDown, MapPin } from 'lucide-react'
import { type JSX, useLayoutEffect, useRef, useState } from 'react'
import {
    formatRegistrationOpensAt,
    formatSessionDateTimeParts,
    formatSessionMonthRange,
    getRegistrationEligibility,
    occurrenceTitle,
    type SessionDateTimeParts,
    type SessionLike
} from './schedule'
import {
    formatEuroPrice,
    locationLabel,
    type SeminarPageColors,
    seminarLocationShortLabel,
    stripHtml,
    tintColor
} from './theme'

export interface SeminarOccurrencesAndBookingProps {
    seminar: PublicSeminarDetailResponse
    colors: SeminarPageColors
    onBook: (occurrence: PublicSeminarOccurrenceSummary) => void
    allSeminarsHref?: string
    onAllSeminars?: () => void
    bottomBarLayout?: 'fixed' | 'sticky'
}

function SessionDateTime({ parts, compact = false }: { parts: SessionDateTimeParts; compact?: boolean }): JSX.Element {
    if (compact) {
        return (
            <div className="text-sm text-foreground">
                <span className="font-bold">{parts.weekday}</span>
                {`, ${parts.date}, `}
                <span className="text-xs font-semibold">{parts.timeRange}</span>
            </div>
        )
    }

    return (
        <div>
            <div className="text-sm text-foreground">
                <span className="font-bold">{parts.weekday}</span>
                {`, ${parts.date}`}
                <span className="sm:hidden">,</span>
            </div>
            <div className="text-xs font-semibold text-foreground">{parts.timeRange}</div>
        </div>
    )
}

function OccurrenceScheduleSummary({
    sessions,
    location,
    className,
    showMonthRange = true
}: {
    sessions: SessionLike[]
    location: string
    className?: string
    showMonthRange?: boolean
}): JSX.Element | null {
    const containerRef = useRef<HTMLParagraphElement>(null)
    const measureRef = useRef<HTMLSpanElement>(null)
    const [stacked, setStacked] = useState(false)

    const countLabel = sessions.length === 1 ? '1 Termin' : `${sessions.length} Termine`
    const primaryLabel = `${location} · ${countLabel}`
    const monthRange = showMonthRange ? formatSessionMonthRange(sessions) : ''

    useLayoutEffect(() => {
        const container = containerRef.current
        const measure = measureRef.current
        if (!container || !measure || !monthRange) {
            setStacked(false)
            return
        }

        const update = () => {
            setStacked(measure.scrollWidth > container.clientWidth)
        }

        update()
        const observer = new ResizeObserver(update)
        observer.observe(container)
        return () => observer.disconnect()
    }, [primaryLabel, monthRange])

    if (sessions.length === 0) {
        return null
    }

    return (
        <p
            ref={containerRef}
            className={cn(
                'relative flex text-xs text-foreground-secondary',
                stacked ? 'flex-col gap-0' : 'flex-wrap gap-x-1.5',
                className
            )}
        >
            {monthRange ? (
                <span ref={measureRef} aria-hidden className="pointer-events-none absolute whitespace-nowrap opacity-0">
                    {primaryLabel} · {monthRange}
                </span>
            ) : null}
            <span>{primaryLabel}</span>
            {monthRange ? (
                <span className="whitespace-nowrap">
                    {stacked ? null : '· '}
                    {monthRange}
                </span>
            ) : null}
        </p>
    )
}

function registrationUnavailableLabel(occurrence: PublicSeminarOccurrenceSummary): string | null {
    const eligibility = getRegistrationEligibility(occurrence)
    if (eligibility.canRegister) {
        return null
    }
    switch (eligibility.reason) {
        case 'not_yet_open':
            return `Anmeldung öffnet am ${formatRegistrationOpensAt(occurrence.registrationOpensAt)}`
        case 'closed':
        case 'status':
            return 'Anmeldung geschlossen'
        case 'full':
            return 'Ausgebucht'
        default:
            return 'Anmeldung nicht möglich'
    }
}

function sortSessions<T extends { start: string; end: string }>(sessions: T[]): T[] {
    return [...sessions].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
}

function isBookableOccurrence(occurrence: PublicSeminarOccurrenceSummary): boolean {
    return getRegistrationEligibility(occurrence).canRegister
}

export function SeminarOccurrencesAndBooking({
    seminar,
    colors,
    onBook,
    allSeminarsHref,
    onAllSeminars,
    bottomBarLayout = 'fixed'
}: SeminarOccurrencesAndBookingProps): JSX.Element {
    const occurrences = seminar.openOccurrences.filter(isBookableOccurrence)
    const [selectedPublicId, setSelectedPublicId] = useState<string | null>(occurrences[0]?.publicId ?? null)
    const selected =
        occurrences.find((occurrence) => occurrence.publicId === selectedPublicId) ?? occurrences[0] ?? null

    const [sessionsExpanded, setSessionsExpanded] = useState(false)

    const showLeaderInSelector =
        new Set(occurrences.map((occurrence) => occurrence.therapistUuid).filter(Boolean)).size > 1

    const leaderName = selected?.therapistName ?? seminar.therapistName
    const leaderImage = selected?.therapistImage ?? seminar.therapistImage
    const leaderTitle = selected?.therapistTitle ?? seminar.therapistTitle
    const leaderDescription = selected?.therapistDescription ?? seminar.therapistDescription

    const allSeminarsLink =
        allSeminarsHref != null ? (
            <a
                href={allSeminarsHref}
                className="underline-offset-2 hover:underline"
                style={{ color: colors.primaryColor }}
            >
                Alle Seminare von {seminar.therapistName}
            </a>
        ) : onAllSeminars != null ? (
            <button
                type="button"
                onClick={onAllSeminars}
                className="underline-offset-2 hover:underline"
                style={{ color: colors.primaryColor }}
            >
                Alle Seminare von {seminar.therapistName}
            </button>
        ) : null

    const therapistSection = leaderName ? (
        <>
            <h2 className="mt-11 text-xl font-bold tracking-tight text-foreground">Leitung des Seminars</h2>
            <div className="mt-4 flex gap-5 rounded-lg border border-border/50 p-5 shadow-xs">
                {leaderImage ? (
                    <img src={leaderImage} alt={leaderName} className="h-24 w-24 shrink-0 rounded-full object-cover" />
                ) : (
                    <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-surface text-2xl font-semibold text-foreground-tertiary">
                        {leaderName.charAt(0)}
                    </div>
                )}
                <div className="min-w-0">
                    <p className="text-lg font-semibold text-foreground">{leaderName}</p>
                    {leaderTitle ? <p className="mt-0.5 text-sm text-foreground-tertiary">{leaderTitle}</p> : null}
                    {leaderDescription ? (
                        <p className="mt-3 text-sm leading-relaxed text-foreground-secondary">
                            {stripHtml(leaderDescription)}
                        </p>
                    ) : null}
                    {allSeminarsLink ? <p className="mt-3 text-sm">{allSeminarsLink}</p> : null}
                </div>
            </div>
        </>
    ) : null

    if (!selected) {
        return (
            <div className="grid gap-10 pb-14 lg:grid-cols-[1.4fr_340px] lg:items-start">
                <div className="order-2 min-w-0 lg:order-1">
                    {seminar.description ? (
                        <div
                            className="tebutoEditor seminarDescription text-foreground-secondary"
                            dangerouslySetInnerHTML={{ __html: seminar.description }}
                        />
                    ) : null}
                    {therapistSection}
                </div>
                <aside className="order-1 lg:order-2 lg:sticky lg:top-24">
                    <div className="rounded-lg border border-border/50 bg-white p-5 shadow-sm">
                        <p className="text-sm leading-relaxed text-foreground-secondary">
                            Aktuell sind keine Veranstaltungen verfügbar. Schauen Sie bald wieder vorbei.
                        </p>
                    </div>
                </aside>
            </div>
        )
    }

    const sessions = sortSessions(selected.sessions)
    const visibleSessions = sessionsExpanded ? sessions : sessions.slice(0, 4)
    const hasThemeColumn = sessions.some((session) => Boolean(session.label?.trim()))
    const canRegister = getRegistrationEligibility(selected).canRegister
    const unavailableLabel = registrationUnavailableLabel(selected)
    const isFree = Number(selected.price) === 0
    const hasMultiplePrices = new Set(occurrences.map((occurrence) => Number(occurrence.price))).size > 1
    const ctaLabel = selected.remainingSeats <= 0 && selected.waitlistEnabled ? 'Auf Warteliste setzen' : 'Anmelden'

    const bottomBarClassName =
        bottomBarLayout === 'sticky'
            ? 'sticky bottom-0 z-30 flex items-center gap-3 border-t border-border-secondary bg-white/95 px-4 py-3 shadow-[0_-4px_12px_rgb(0_0_0/0.06)] backdrop-blur lg:hidden'
            : 'fixed inset-x-0 bottom-0 z-30 flex items-center gap-3 border-t border-border-secondary bg-white/95 px-4 py-3 shadow-[0_-4px_12px_rgb(0_0_0/0.06)] backdrop-blur lg:hidden'

    return (
        <>
            <div className="grid gap-10 pb-14 lg:grid-cols-[1.4fr_340px] lg:items-start">
                <div className="order-2 min-w-0 lg:order-1">
                    {seminar.description ? (
                        <div
                            className="tebutoEditor seminarDescription text-foreground-secondary"
                            dangerouslySetInnerHTML={{ __html: seminar.description }}
                        />
                    ) : null}

                    <h2 className="mt-11 mb-4 text-xl font-bold tracking-tight text-foreground">Termine</h2>
                    <div className="overflow-hidden rounded-lg border border-border/50 shadow-xs">
                        {hasThemeColumn ? (
                            <div className="hidden border-b border-border/50 bg-surface-subtle px-4 py-2.5 text-xxs font-semibold uppercase tracking-wider text-foreground-tertiary sm:grid sm:grid-cols-[9rem_1fr] sm:gap-4">
                                <div>Termin</div>
                                <div>Thema</div>
                            </div>
                        ) : null}
                        {visibleSessions.map((session, index) => {
                            const dateTimeParts = formatSessionDateTimeParts(session.start, session.end)
                            return (
                                <div
                                    key={session.id}
                                    className={cn(
                                        'px-4 text-sm text-foreground-secondary',
                                        hasThemeColumn
                                            ? 'flex flex-col gap-1 py-3 sm:grid sm:grid-cols-[9rem_1fr] sm:items-start sm:gap-4'
                                            : 'py-2.5',
                                        index > 0 && 'border-t border-border/50'
                                    )}
                                >
                                    {hasThemeColumn ? (
                                        <div className="order-1 font-medium text-foreground sm:order-2">
                                            {session.label?.trim() || '—'}
                                        </div>
                                    ) : null}
                                    {dateTimeParts ? (
                                        <div className={cn(hasThemeColumn && 'order-2 sm:order-1')}>
                                            <SessionDateTime parts={dateTimeParts} compact={!hasThemeColumn} />
                                        </div>
                                    ) : null}
                                </div>
                            )
                        })}
                        {sessions.length > visibleSessions.length ? (
                            <div className="flex justify-center border-t border-border/50 bg-surface-subtle py-2">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    icon={ChevronDown}
                                    iconPosition="after"
                                    onClick={() => setSessionsExpanded(true)}
                                    style={{ color: colors.primaryColor }}
                                >
                                    Alle {sessions.length} Termine anzeigen
                                </Button>
                            </div>
                        ) : null}
                    </div>

                    {therapistSection}

                    {selected.locationType !== 'virtual' ? (
                        <>
                            <h2 className="mt-11 text-xl font-bold tracking-tight text-foreground">Ort</h2>
                            <div className="mt-3 flex items-start gap-2 text-sm text-foreground-secondary">
                                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-foreground-tertiary" />
                                <div>
                                    <p className="font-medium text-foreground">
                                        {locationLabel(selected.locationType, selected.locationName, selected.cityName)}
                                    </p>
                                    {selected.streetAndNumber ? (
                                        <p>
                                            {selected.streetAndNumber}
                                            {selected.cityZip || selected.cityName
                                                ? `, ${[selected.cityZip, selected.cityName].filter(Boolean).join(' ')}`
                                                : ''}
                                        </p>
                                    ) : null}
                                    {selected.additionalInformation ? <p>{selected.additionalInformation}</p> : null}
                                </div>
                            </div>
                        </>
                    ) : null}
                </div>

                <aside className="order-1 lg:order-2 lg:sticky lg:top-24">
                    <div className="rounded-lg border border-border/50 bg-white p-5 shadow-sm">
                        <div className="flex items-baseline gap-2">
                            <span className="text-3xl font-extrabold tracking-tight text-foreground">
                                {formatEuroPrice(selected.price)}
                            </span>
                            {!isFree ? <span className="text-xs text-foreground-tertiary">pro Person</span> : null}
                        </div>
                        {selected.taxRate ? (
                            <p className={cn('mt-1 text-xxs text-foreground-tertiary', isFree && 'invisible')}>
                                inkl. {Number.parseInt(selected.taxRate, 10)} % MwSt.
                            </p>
                        ) : null}

                        {occurrences.length > 1 ? (
                            <>
                                <p className="mb-2 mt-5 text-xxs font-semibold uppercase tracking-wider text-foreground-tertiary">
                                    Veranstaltung wählen
                                </p>
                                <div className="flex flex-col gap-2">
                                    {occurrences.map((occurrence) => {
                                        const isSelected = occurrence.publicId === selected.publicId
                                        const showPrice = hasMultiplePrices
                                        return (
                                            <button
                                                key={occurrence.publicId}
                                                type="button"
                                                data-testid={`seminar-occurrence-${occurrence.publicId}`}
                                                onClick={() => setSelectedPublicId(occurrence.publicId)}
                                                className={cn(
                                                    'rounded-lg border p-3 text-left transition-colors',
                                                    !isSelected && 'border-border-secondary'
                                                )}
                                                style={
                                                    isSelected
                                                        ? {
                                                              borderColor: colors.primaryColor,
                                                              backgroundColor: tintColor(colors.primaryColor)
                                                          }
                                                        : undefined
                                                }
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span
                                                        className={cn(
                                                            'inline-block h-4 w-4 shrink-0 rounded-full border',
                                                            isSelected ? 'border-4' : 'border border-border-secondary'
                                                        )}
                                                        style={
                                                            isSelected
                                                                ? { borderColor: colors.primaryColor }
                                                                : undefined
                                                        }
                                                    />
                                                    <span className="text-sm font-semibold text-foreground">
                                                        {occurrenceTitle(occurrence)}
                                                    </span>
                                                    {showPrice ? (
                                                        <span className="text-sm font-normal text-foreground-tertiary">
                                                            {formatEuroPrice(occurrence.price)}
                                                        </span>
                                                    ) : null}
                                                </div>
                                                {showLeaderInSelector && occurrence.therapistName ? (
                                                    <p className="ml-6 mt-0.5 text-xs text-foreground-tertiary">
                                                        Leitung: {occurrence.therapistName}
                                                    </p>
                                                ) : null}
                                                <OccurrenceScheduleSummary
                                                    sessions={occurrence.sessions}
                                                    location={seminarLocationShortLabel(
                                                        occurrence.locationType,
                                                        occurrence.locationName,
                                                        occurrence.cityName
                                                    )}
                                                    className="ml-6 mt-1"
                                                    showMonthRange={Boolean(occurrence.label?.trim())}
                                                />
                                            </button>
                                        )
                                    })}
                                </div>
                            </>
                        ) : (
                            <div
                                data-testid={`seminar-occurrence-${selected.publicId}`}
                                className="mt-5 rounded-lg border p-3"
                                style={{
                                    borderColor: colors.primaryColor,
                                    backgroundColor: tintColor(colors.primaryColor)
                                }}
                            >
                                <p className="text-sm font-semibold text-foreground">{occurrenceTitle(selected)}</p>
                                {showLeaderInSelector && selected.therapistName ? (
                                    <p className="mt-0.5 text-xs text-foreground-tertiary">
                                        Leitung: {selected.therapistName}
                                    </p>
                                ) : null}
                                <OccurrenceScheduleSummary
                                    sessions={selected.sessions}
                                    location={seminarLocationShortLabel(
                                        selected.locationType,
                                        selected.locationName,
                                        selected.cityName
                                    )}
                                    className="mt-1"
                                    showMonthRange={Boolean(selected.label?.trim())}
                                />
                            </div>
                        )}

                        <Button
                            className="mt-4 w-full"
                            onClick={() => onBook(selected)}
                            disabled={!canRegister}
                            data-testid="seminar-booking-cta"
                            style={{ backgroundColor: colors.primaryColor }}
                        >
                            {ctaLabel}
                        </Button>
                        {unavailableLabel ? (
                            <p className="mt-2 text-center text-xxs text-foreground-secondary">{unavailableLabel}</p>
                        ) : (
                            <p className="mt-3 text-center text-xxs leading-relaxed text-foreground-tertiary">
                                Verbindlich erst nach Bestätigung per E-Mail.
                            </p>
                        )}
                    </div>
                </aside>
            </div>

            <div className={bottomBarClassName}>
                <div className="min-w-0">
                    <p className="text-sm font-bold text-foreground">{formatEuroPrice(selected.price)}</p>
                    <p className="mt-0.5 flex items-center gap-1 text-xxs text-foreground-tertiary">
                        <CalendarDays className="h-3 w-3" />
                        {occurrenceTitle(selected)}
                    </p>
                </div>
                <div className="ml-auto flex flex-col items-end gap-1">
                    <Button
                        onClick={() => onBook(selected)}
                        disabled={!canRegister}
                        style={{ backgroundColor: colors.primaryColor }}
                    >
                        {ctaLabel}
                    </Button>
                    {unavailableLabel ? (
                        <p className="text-right text-xxs text-foreground-secondary">{unavailableLabel}</p>
                    ) : null}
                </div>
            </div>
        </>
    )
}
